import React from 'react';
import classNames from 'classnames';
import { kebabCase } from 'lodash/fp';
import { useDispatch } from 'react-redux';

// Components
import Icon from '../../../../components/icons/Icon';
import Button from '../../../../components/buttons/Button';
import FormattingOptionsPopup from './FormattingOptionsPopup';

// Manager
import { useTranslator } from '../../../../components/i18n/useTranslator';
import { useLegacyActiveTableCallback, useLegacyActiveTableSelector } from '../../store/legacyHotTableStoreHooks';
import { updateTableCellType, updateTableCellTypeFormat } from '../actions/hotTableActions';
import { getNewTransactionId } from '../../../../utils/undoRedo/undoRedoTransactionManager';

// Utils
import { getElementId } from '../../../../../common/elements/utils/elementPropertyUtils';
import { cellTypeUpdateShouldHappen } from '../utils/tableCellTypeUtils';
import { getNormalisedHotCellValueFromHot } from '../utils/tableCellFormattingUtils';
import { translateCellType } from '../../../../../common/table/cellTypeTranslations';

// Constants
import { CellTypeConstants } from '../../../../../common/table/CellTypeConstants';
import { CellTypeObject } from '../../../../../common/table/TableTypes';

// Styles
import './CellTypePopup.scss';

type CellTypeButtonProps = {
    currentCellType: keyof typeof CellTypeConstants;
    dispatchUpdateCellType?: (elementId: string, cellType: string) => void;
    element: object;
    isActive: boolean;
    allSelectedCells: Array<{ row: number; col: number }>;
    lastClickedType: string;
    setLastClickedType: (type: string) => void;
    locale: string;
};

const CellTypeButton: React.FC<CellTypeButtonProps> = (props) => {
    const { element, currentCellType, isActive, allSelectedCells, lastClickedType, setLastClickedType, locale } = props;
    const { icon, formattingOptions } = CellTypeConstants[currentCellType];

    const translate = useTranslator('popup', 'table');

    // @ts-ignore Couldn't figure out the type here
    const displayName = translateCellType(currentCellType, translate);
    const elementId = getElementId(element);

    // Does type have formatting options AND
    // do all the selected cells have the current cell type, OR is it the last clicked cell type?
    const showFormattingOptions = !!formattingOptions && (isActive || lastClickedType === currentCellType);

    const isDisabled = useLegacyActiveTableSelector((activeTableInstance) => {
        if (!activeTableInstance) return true;
        return !allSelectedCells.some(({ row, col }) => {
            const normalisedHotCellValue = getNormalisedHotCellValueFromHot(elementId, row, col, activeTableInstance);
            return cellTypeUpdateShouldHappen(normalisedHotCellValue, currentCellType, locale);
        });
    });

    const dispatch = useDispatch();

    const updateCellType = useLegacyActiveTableCallback(
        (activeTable) => {
            dispatch(
                updateTableCellType({
                    hotInstance: activeTable,
                    elementId,
                    cellType: currentCellType,
                    transactionId: getNewTransactionId(),
                }),
            );
            setLastClickedType(currentCellType);
        },
        [elementId, currentCellType],
    );

    const updateCellTypeFormat = useLegacyActiveTableCallback(
        (activeTable, typeFormatChanges: CellTypeObject) =>
            dispatch(
                updateTableCellTypeFormat({
                    hotInstance: activeTable,
                    elementId,
                    typeFormatChanges,
                    transactionId: getNewTransactionId(),
                }),
            ),
        [elementId],
    );

    return (
        <>
            <Button
                onClick={updateCellType}
                className={classNames('CellTypeButton', kebabCase(currentCellType), {
                    active: isActive,
                    disabled: isDisabled,
                })}
            >
                <Icon name={icon} />
                <span>{displayName}</span>
                {isActive && <Icon name={'block-style-tick'} />}
                {!isActive && !!formattingOptions && <Icon name="chevron-right" />}
            </Button>
            {showFormattingOptions && (
                <FormattingOptionsPopup
                    {...props}
                    showFormattingOptions={showFormattingOptions}
                    updateCellTypeFormat={updateCellTypeFormat}
                />
            )}
        </>
    );
};

type CellTypePopupProps = Omit<CellTypeButtonProps, 'currentCellType' | 'isActive'> & {
    activeCellType: string;
    closePopup: (() => void) | null;
};

const CellTypePopup: React.FC<CellTypePopupProps> = (props) => {
    const { activeCellType } = props;
    return (
        <>
            {Object.keys(CellTypeConstants).map((currentCellType) => (
                <CellTypeButton
                    {...props}
                    key={currentCellType}
                    currentCellType={currentCellType as keyof typeof CellTypeConstants}
                    isActive={activeCellType === currentCellType}
                />
            ))}
        </>
    );
};

export default CellTypePopup;
